import { ChevronRight, Search, Star } from "lucide-react";
import { Fragment, useId, useState } from "react";
import { Link, useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { useTalkNowMutation } from "@/api/tenantRuntime";
import {
  useTenantsQuery,
  type TenantProject,
  type TenantSummary,
} from "@/api/tenants";
import { useSession } from "@/auth/SessionContext";
import { useRuntimeConfig } from "@/config/RuntimeConfigContext";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import { ENVIRONMENT_LABEL } from "@/lib/labels";
import { publicationBadgeLabel, readinessLabel } from "@/lib/projectStatus";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { Input } from "@/ui/Input";
import { Tooltip } from "@/ui/Tooltip";

import { TenantEnvironmentsTable } from "./TenantEnvironmentsTable";

const projectPath = (id: string) => `/projects/${encodeURIComponent(id)}`;

/** The two statuses the Projects list shows for a Project, as the same badges. */
function ProjectStatus({ project }: { project: TenantProject }) {
  const publication = publicationBadgeLabel(project.publicationState);
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Badge tone="neutral">{readinessLabel(project.readiness)}</Badge>
      {publication ? <Badge tone="outline">{publication}</Badge> : null}
    </span>
  );
}

function LiveNow({ tenant }: { tenant: TenantSummary }) {
  const live = tenant.live;
  if (live === null) {
    return (
      <span className="text-xs text-muted-foreground">
        Couldn&apos;t reach this tenant
      </span>
    );
  }
  if (live.project) {
    return (
      <Link
        to={projectPath(live.project.id)}
        className="font-semibold text-primary hover:underline"
        dir="auto"
      >
        {live.project.name}
      </Link>
    );
  }
  return (
    <span className="font-mono text-xs">
      {live.packRevision
        ? `${live.packName} @ ${live.packRevision}`
        : live.packName}
    </span>
  );
}

function Picked({ tenant }: { tenant: TenantSummary }) {
  if (tenant.picked === null) {
    return (
      <span className="text-xs text-muted-foreground">Nothing picked</span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Star
        aria-hidden="true"
        className="h-3.5 w-3.5 fill-current text-warning-foreground"
      />
      <span dir="auto">{tenant.picked.name}</span>
      {/* Only a reachable tenant can be said not to run its pick. */}
      {tenant.live !== null && !tenant.pickLive ? (
        <Badge tone="warning" title="Picked, but not what this tenant runs">
          ⚠ not live
        </Badge>
      ) : null}
    </span>
  );
}

function TalkNow({ tenant }: { tenant: TenantSummary }) {
  const navigate = useNavigate();
  const talk = useTalkNowMutation();
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Button
        variant="outline"
        size="sm"
        disabled={talk.isPending}
        onClick={() =>
          talk.mutate(tenant.previewTenantId, {
            onSuccess: (result) => navigate(result.labPath),
          })
        }
      >
        {talk.isPending ? "Preparing…" : "Talk now"}
      </Button>
      {talk.isError ? (
        <span role="alert" className="text-xs text-destructive">
          {talk.error instanceof ApiError
            ? talk.error.message
            : "Talk now failed. Try again."}
        </span>
      ) : null}
    </span>
  );
}

function ViewFiles({ tenant }: { tenant: TenantSummary }) {
  const live = tenant.live;
  if (live === null) {
    return null;
  }
  // A live Editor Project has its own Pack files page.
  const to = live.project
    ? `${projectPath(live.project.id)}/config/files`
    : tenant.filesAvailable
      ? `/tenants/${encodeURIComponent(tenant.previewTenantId)}/files`
      : null;
  if (to === null) {
    return (
      <Tooltip
        content={
          tenant.filesUnavailableReason ??
          "The editor's copy differs from what this tenant runs."
        }
      >
        <Button
          variant="outline"
          size="sm"
          disabled
          aria-label={`View ${tenant.previewTenantId} files`}
        >
          View files
        </Button>
      </Tooltip>
    );
  }
  return (
    <Button asChild variant="outline" size="sm">
      <Link to={to} aria-label={`View ${tenant.previewTenantId} files`}>
        View files
      </Link>
    </Button>
  );
}

function matches(tenant: TenantSummary, needle: string): boolean {
  return [
    tenant.previewTenantId,
    tenant.live?.packName,
    tenant.live?.project?.name,
    ...tenant.projects.map((project) => project.name),
  ].some((value) => value?.toLowerCase().includes(needle));
}

/**
 * The Projects page's Tenants tab (#1165): one row per preview target of this
 * environment — what it runs now, the Project picked for it, its Projects, and
 * a way to read the pack files it runs. Read-only.
 *
 * "Live now" is the tenant runtime's own answer, asked on every read; a tenant
 * that does not answer is its own row and never hides the others.
 */
export function TenantsPanel() {
  const { session } = useSession();
  const { tenantTools } = useRuntimeConfig().features;
  const environment =
    ENVIRONMENT_LABEL[session.environment] ?? session.environment;
  const tenants = useTenantsQuery();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const searchId = useId();

  const toggle = (tenantId: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(tenantId)) next.add(tenantId);
      return next;
    });

  const needle = search.trim().toLowerCase();
  const rows = (tenants.data?.items ?? []).filter(
    (tenant) => needle === "" || matches(tenant, needle),
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-base font-semibold">Tenants in {environment}</h2>
        <span className="text-xs text-muted-foreground">
          Live packs and picks in this environment only. Each environment has
          its own editor and picks.
        </span>
      </div>
      <label htmlFor={searchId} className="sr-only">
        Search tenants or projects
      </label>
      <Input
        id={searchId}
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search tenants or projects"
        leadingIcon={<Search className="h-4 w-4" />}
        className="max-w-sm"
      />

      {tenants.isPending ? (
        <QueryPendingState
          isPaused={tenants.isPaused}
          failureReason={tenants.failureReason}
          label="Loading tenants"
          errorTitle="Tenants could not be loaded"
          rows={4}
          queryKey={queryKeys.tenants}
        />
      ) : tenants.isError ? (
        <QueryErrorState
          error={tenants.error}
          subject="Tenants"
          title="Tenants could not be loaded"
          description="The Editor Service did not return the tenant list."
          deniedDescription="You are not authorized to list tenants in this environment."
          onRetry={() => void tenants.refetch()}
        />
      ) : (
        <>
          <Card className="overflow-x-auto">
            <table className="w-full min-w-[38rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-semibold text-muted-foreground">
                  <th scope="col" className="px-3 py-2.5">
                    Tenant
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Live now
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Picked
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    Projects
                  </th>
                  <th scope="col" className="px-3 py-2.5">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-8 text-center text-muted-foreground"
                    >
                      No tenants match.
                    </td>
                  </tr>
                ) : (
                  rows.map((tenant) => {
                    const count = tenant.projects.length;
                    const expanded =
                      count > 0 && open.has(tenant.previewTenantId);
                    return (
                      <Fragment key={tenant.previewTenantId}>
                        <tr className="border-b border-border hover:bg-muted/50">
                          <td className="px-3 py-2">
                            <span className="flex items-center gap-1.5 font-semibold whitespace-nowrap">
                              <button
                                type="button"
                                aria-label={`Show ${tenant.previewTenantId} projects`}
                                aria-expanded={expanded}
                                disabled={count === 0}
                                onClick={() => toggle(tenant.previewTenantId)}
                                className={cn(
                                  "text-muted-foreground disabled:invisible",
                                  expanded && "rotate-90",
                                )}
                              >
                                <ChevronRight
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              </button>
                              <span dir="auto">{tenant.previewTenantId}</span>
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <LiveNow tenant={tenant} />
                          </td>
                          <td className="px-3 py-2">
                            <Picked tenant={tenant} />
                          </td>
                          <td className="px-3 py-2">
                            {count > 0 ? (
                              `${count} ${count === 1 ? "project" : "projects"}`
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                None
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className="inline-flex items-start gap-2">
                              {tenantTools && tenant.live !== null ? (
                                <TalkNow tenant={tenant} />
                              ) : null}
                              <ViewFiles tenant={tenant} />
                            </span>
                          </td>
                        </tr>
                        {expanded
                          ? tenant.projects.map((project) => (
                              <tr
                                key={project.id}
                                className="border-b border-border bg-background"
                              >
                                <td className="py-1.5 pr-3 pl-10">
                                  <Link
                                    to={projectPath(project.id)}
                                    className="font-semibold text-primary hover:underline"
                                    dir="auto"
                                  >
                                    {project.name}
                                  </Link>
                                </td>
                                <td className="px-3 py-1.5">
                                  <ProjectStatus project={project} />
                                </td>
                                <td className="px-3 py-1.5 text-xs">
                                  {tenant.picked?.id === project.id ? (
                                    <span className="flex items-center gap-1">
                                      <Star
                                        aria-hidden="true"
                                        className="h-3.5 w-3.5 fill-current text-warning-foreground"
                                      />
                                      picked
                                    </span>
                                  ) : null}
                                </td>
                                <td colSpan={2} />
                              </tr>
                            ))
                          : null}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </Card>
          <p className="text-xs text-muted-foreground">
            Live now is read from each tenant&apos;s runtime, checked{" "}
            {formatDateTime(tenants.data.observedAt)}. ⚠ marks a picked project
            that isn&apos;t live yet.
          </p>
        </>
      )}
      <TenantEnvironmentsTable />
    </div>
  );
}
